import { ContextMenuTriggerProps } from "@radix-ui/react-context-menu";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { LoadingSpinner } from "./ui/loading-spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuPortal
} from "./ui/context-menu";
import { ChevronRight, ChevronDown } from "lucide-react";
import axios from "axios";
import React, { ChangeEvent, Ref, useEffect, useState } from "react";
import {Matter, Contact, ContactInfo} from "src/lib/conflix";
import SheetCell from "./sheetCell";
import StatusCell from "./statusCell";

const apiUrl = process.env.REACT_APP_API_URL;

function MatterRow(props: {
  hasPro: boolean;
  matter: Matter;
  contact: Contact;
  designation: string;
  info?: ContactInfo;
  primaryContactInfoIds?: Record<string, string>;
  idToken: string;
  onLoading: (isLoading: boolean) => void;
  onUpdate: () => Promise<void>;
  onError: (err: string) => void;
}) {
  const [editedField, setEditedField] = useState<string>("");
  const [refNumberValue, setRefNumberValue] = useState<string>(
    props.matter.referenceNumber
  );
  const [nameValue, setNameValue] = useState<string>(props.matter.name);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [loadedInfo, setLoadedInfo] = useState<ContactInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState<boolean>(false);

  const hasContactInfo = props.primaryContactInfoIds &&
    Object.values(props.primaryContactInfoIds).some(v => v);

  const onToggleExpand = async () => {
    if (!expanded && !loadedInfo && hasContactInfo) {
      setLoadingInfo(true);
      try {
        const response = await axios.get(
          `${apiUrl}matters/${props.matter.matterId}/contacts/${props.contact.contactId}/info`,
          {
            headers: {
              Authorization: props.idToken,
            },
          }
        );
        setLoadedInfo(response.data);
      } catch (error) {
        console.error("Error fetching contact info:", error);
        props.onError("Failed to load contact info");
      }
      setLoadingInfo(false);
    }
    setExpanded(!expanded);
  };

  const effectiveInfo = loadedInfo || props.info;

  const onDelete = async () => {
    props.onLoading(true);
    const response = await axios.delete(
      `${apiUrl}matters/${props.matter.matterId}/contacts/${props.contact.contactId}`,
      {
        headers: {
          Authorization: props.idToken,
          "Content-Type": "application/json",
        },
        validateStatus: () => true
      }
    );
    if (!(response.status >= 200 && response.status < 300)) {
      let errMessage = response.data.message;
      if (response.data.details) {
          errMessage = `${errMessage}: ${response.data.details}`;
      }
      props.onError(errMessage);
    }
    props.onLoading(false);
  }

  const viewMatterDetails = () => {
    window.history.pushState({}, '', `/matter/${props.matter.matterId}`);
    // Trigger a popstate event to update the view in App.tsx
    window.dispatchEvent(new Event('popstate'));
  }

  return (
    <>
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TableRow className="hover:bg-gray-50">
          <TableCell className="w-8 p-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              disabled={loadingInfo}
              className="h-6 w-6"
              aria-label={
                loadingInfo
                  ? "Loading matter details"
                  : expanded
                  ? "Collapse row"
                  : "Expand row"
              }
            >
              {loadingInfo ? (
                <LoadingSpinner size={16} />
              ) : expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </TableCell>
          <SheetCell field="referenceNumber" matter={props.matter} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
          <SheetCell field="name" matter={props.matter} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
          <SheetCell field="designation" contact={props.contact} matter={props.matter} designation={props.designation} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
          <SheetCell field="firstName" contact={props.contact} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
          <SheetCell field="lastName" contact={props.contact} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
          <SheetCell field="middleName" contact={props.contact} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
          <SheetCell field="companyName" contact={props.contact} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
          <SheetCell field="dateOfBirth" contact={props.contact} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
          <StatusCell matter={props.matter} info={props.info} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
        </TableRow>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {props.hasPro && <ContextMenuItem onClick={viewMatterDetails} inset>View Details</ContextMenuItem>}
        <ContextMenuItem onClick={onDelete} inset>Delete</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
    {expanded && (
      <TableRow className="bg-gray-50">
        <TableCell />
        <TableCell className="text-muted-foreground text-right">Phone</TableCell>
        <SheetCell field="phone" contact={props.contact} matter={props.matter} info={effectiveInfo} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
        <TableCell className="text-muted-foreground text-right">Email</TableCell>
        <SheetCell field="email" contact={props.contact} matter={props.matter} info={effectiveInfo} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
        <TableCell className="text-muted-foreground text-right">Address</TableCell>
        <SheetCell field="address" contact={props.contact} matter={props.matter} info={effectiveInfo} idToken={props.idToken} onLoading={props.onLoading} onUpdate={props.onUpdate} onError={props.onError}/>
        <TableCell colSpan={3} />
      </TableRow>
    )}
    </>
  );
}

export default MatterRow;
